import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Layers, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AdminPage,
  ConfirmActionDialog,
  EmptyState,
  SearchField,
  SectionCard,
  StatusBadge,
  formatNumber,
} from '../../../components/design-system/admin';
import { useProductTypesApi } from '../../../common/hooks/use-product-types-api.hook';
import {
  setProductTypes,
  setProductTypesLoading,
  deleteProductType as deleteProductTypeAction,
} from '../../../redux/productTypes/productTypesActions';
import { ProductType } from '../../../common/models/product-type.model';
import { VendorState } from '../../../redux/vendor/rootReducer';
import { useResolveTranslation } from '../../../common/hooks/use-resolve-translation.hook';
import { resolveTranslation } from '../../../common/utils/translatable-string';
import { CatalogRowActions } from '../../../modules/superadmin/catalog-config';

/**
 * Product types: the kinds of surface product (tile, hardwood, stone…), where
 * each can be laid and the patterns it comes in. Each row opens the editor.
 */
export const ProductTypes = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { rt } = useResolveTranslation();
  const productTypesApi = useProductTypesApi();

  const { types, loading } = useSelector(
    (state: VendorState) => state.productTypes
  );

  const [filter, setFilter] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<ProductType | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchTypes = useCallback(async () => {
    setLoadFailed(false);
    dispatch(setProductTypesLoading(true));
    try {
      const data = await productTypesApi.findAllProductTypes();
      dispatch(setProductTypes(data));
    } catch (error) {
      console.error('Error fetching product types:', error);
      // The spinner used to stay forever on a failed load.
      dispatch(setProductTypesLoading(false));
      setLoadFailed(true);
    } finally {
      setFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const query = filter.trim().toLowerCase();
  const filteredTypes = (types || []).filter(
    pt => {
      if (!pt || !pt.name) return false;
      if (!query) return true;
      const haystack = [resolveTranslation(pt.name, 'en'), rt(pt.name), pt.slug || ''];
      return haystack.some(s => s.toLowerCase().includes(query));
    }
  );

  const handleAddType = () => {
    navigate('/product-types/new');
  };

  const handleEditType = (pt: ProductType) => {
    navigate(`/product-types/${pt._id}/edit`);
  };

  const handleDeleteClick = (pt: ProductType) => {
    if (pt.isSystem) return;
    setTypeToDelete(pt);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!typeToDelete) return;
    setDeleting(true);
    try {
      await productTypesApi.deleteProductType(typeToDelete._id);
      dispatch(deleteProductTypeAction(typeToDelete._id));
      setShowDeleteDialog(false);
      setTypeToDelete(null);
      toast.success(t('ADMIN_CATALOG.TYPES.DELETED'));
    } catch (error) {
      console.error('Error deleting product type:', error);
      toast.error(t('ADMIN_CATALOG.TYPES.DELETE_FAILED'));
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setTypeToDelete(null);
  };

  const showSkeleton = loading || (!fetched && (types || []).length === 0);
  const total = (types || []).filter(pt => pt && pt.name).length;

  const addButton = (
    <Button onClick={handleAddType}>
      <Plus />
      {t('ADMIN_CATALOG.TYPES.ADD')}
    </Button>
  );

  return (
    <AdminPage
      title={t('ADMIN_SHELL.NAV.TYPES')}
      description={t('ADMIN_CATALOG.TYPES.DESCRIPTION')}
      actions={addButton}
    >
      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchField
          value={filter}
          onChange={setFilter}
          placeholder={t('ADMIN_CATALOG.TYPES.SEARCH_PLACEHOLDER')}
        />
        {!showSkeleton && !loadFailed && total > 0 && (
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="types-count">
            {query
              ? t('ADMIN_CATALOG.COMMON.SHOWN_OF', { shown: filteredTypes.length, total })
              : t('ADMIN_CATALOG.TYPES.COUNT', { count: total })}
          </span>
        )}
      </div>

      {loadFailed && !showSkeleton && total === 0 ? (
        <SectionCard>
          <EmptyState
            title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
            action={
              <Button variant="outline" onClick={fetchTypes}>
                {t('ADMIN_SHELL.COMMON.RETRY')}
              </Button>
            }
          />
        </SectionCard>
      ) : showSkeleton ? (
        <SectionCard flush className="pt-0" testId="types-loading">
          <div className="flex flex-col divide-y">
            {[0, 1, 2].map(i => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="ms-auto h-4 w-16" />
              </div>
            ))}
          </div>
        </SectionCard>
      ) : filteredTypes.length === 0 ? (
        <SectionCard>
          {query ? (
            <EmptyState
              icon={<Layers />}
              title={t('ADMIN_CATALOG.COMMON.NO_MATCH_TITLE', { query: filter.trim() })}
              description={t('ADMIN_CATALOG.COMMON.NO_MATCH_BODY')}
              action={
                <Button variant="outline" onClick={() => setFilter('')}>
                  {t('ADMIN_SHELL.COMMON.CLEAR')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<Layers />}
              title={t('ADMIN_CATALOG.TYPES.EMPTY_TITLE')}
              description={t('ADMIN_CATALOG.TYPES.EMPTY_BODY')}
              action={addButton}
            />
          )}
        </SectionCard>
      ) : (
        <SectionCard flush className="pt-0" testId="types-table">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="ps-4 text-start">{t('ADMIN_CATALOG.TYPES.COL_TYPE')}</TableHead>
                <TableHead className="text-start">{t('ADMIN_CATALOG.COMMON.COL_SLUG')}</TableHead>
                <TableHead className="text-start">{t('ADMIN_CATALOG.TYPES.COL_APPLICATIONS')}</TableHead>
                <TableHead className="text-end">{t('ADMIN_CATALOG.TYPES.COL_PATTERNS')}</TableHead>
                <TableHead className="text-end">{t('ADMIN_CATALOG.TYPES.COL_ORDER')}</TableHead>
                <TableHead className="text-start">{t('ADMIN_CATALOG.COMMON.COL_STATUS')}</TableHead>
                <TableHead className="w-12 pe-4">
                  <span className="sr-only">{t('ADMIN_SHELL.COMMON.ACTIONS')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTypes.map(pt => {
                const name = rt(pt.name);
                return (
                  <TableRow
                    key={pt._id}
                    className={cn('cursor-pointer', !pt.active && 'text-muted-foreground')}
                    onClick={() => handleEditType(pt)}
                    data-testid="type-row"
                  >
                    <TableCell className="ps-4">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Link
                          to={`/product-types/${pt._id}/edit`}
                          onClick={e => e.stopPropagation()}
                          className="text-sm font-medium hover:underline"
                        >
                          {name}
                        </Link>
                        {pt.isSystem && <Badge variant="outline">{t('ADMIN_CATALOG.COMMON.SYSTEM')}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{pt.slug}</TableCell>
                    <TableCell>
                      {pt.applicationTypes?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {pt.applicationTypes.map(appType => (
                            <Badge key={appType} variant="secondary">
                              {t(`ADMIN_CATALOG.APPLICATION_TYPES.${appType.toUpperCase()}`, {
                                defaultValue: appType.charAt(0).toUpperCase() + appType.slice(1),
                              })}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-end text-sm tabular-nums">
                      {formatNumber(pt.patterns?.length || 0)}
                    </TableCell>
                    <TableCell className="text-end text-sm text-muted-foreground tabular-nums">
                      {formatNumber(pt.order ?? 0)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge tone={pt.active ? 'success' : 'neutral'}>
                        {pt.active ? t('ADMIN_CATALOG.COMMON.ACTIVE') : t('ADMIN_CATALOG.COMMON.INACTIVE')}
                      </StatusBadge>
                    </TableCell>
                    {/* The menu's clicks (portalled, but bubbling through React) must not open the row. */}
                    <TableCell className="pe-4 text-end" onClick={e => e.stopPropagation()}>
                      <CatalogRowActions
                        name={name}
                        onEdit={() => handleEditType(pt)}
                        onDelete={() => handleDeleteClick(pt)}
                        deleteBlockedReason={pt.isSystem ? t('ADMIN_CATALOG.TYPES.SYSTEM_LOCKED') : undefined}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </SectionCard>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmActionDialog
        open={showDeleteDialog}
        onOpenChange={open => !open && handleCancelDelete()}
        title={t('ADMIN_CATALOG.TYPES.DELETE_TITLE')}
        description={t('ADMIN_CATALOG.TYPES.DELETE_DESCRIPTION', { name: rt(typeToDelete?.name) })}
        confirmLabel={t('ADMIN_CATALOG.TYPES.DELETE_CONFIRM')}
        destructive
        busy={deleting}
        onConfirm={handleConfirmDelete}
      />
    </AdminPage>
  );
};

export default ProductTypes;
